/**
 * VÉRAULT — full-screen image lightbox (tap-to-expand).
 * Calm, dark, dismiss on backdrop / Esc / close. Shows caption + credit.
 */
import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { pexelsAt, focalPosition, imageUrlOf } from "../utils/imageSrc";

export default function ImageLightbox({ image, open, onClose }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;
  const url = imageUrlOf(image);

  return createPortal(
    <AnimatePresence>
      {open && url && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] bg-black/92 flex flex-col items-center justify-center p-4 sm:p-8"
          onClick={onClose}
          data-testid="image-lightbox"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
            data-testid="image-lightbox-close"
            aria-label="Close"
          >
            <X className="size-5" strokeWidth={1.5} />
          </button>
          <motion.img
            initial={{ scale: 0.97 }} animate={{ scale: 1 }}
            src={pexelsAt(url, 1920)}
            alt={image?.alt || ""}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[82vh] object-contain rounded-md shadow-2xl"
            style={{ objectPosition: focalPosition(image) }}
          />
          {(image?.caption || image?.credit) && (
            <div className="mt-4 text-center" onClick={(e) => e.stopPropagation()}>
              {image?.caption && <p className="font-serif text-[14px] text-white/90">{image.caption}</p>}
              {image?.credit && <p className="mt-1 font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/50">{image.credit}</p>}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
